import { useEffect, useId, useState, type ReactNode } from "react";
import { CalendarClock, Phone, X } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ensureGhlPreconnect } from "@/lib/ghl-preconnect";
import { SITE } from "@/lib/site";
import { phCapture } from "@/lib/posthog";

const BOOKING_URL =
  "https://api.leadconnectorhq.com/widget/bookings/book-a-call-with-crs-energy";
const EMBED_SCRIPT = "https://link.msgsndr.com/js/form_embed.js";

function loadEmbedScript() {
  if (typeof document === "undefined") return;
  ensureGhlPreconnect();
  if (document.querySelector('script[src*="form_embed.js"]')) return;
  const s = document.createElement("script");
  s.src = EMBED_SCRIPT;
  s.type = "text/javascript";
  s.async = true;
  document.body.appendChild(s);
}

export function BookACallDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const descriptionId = useId();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (open) {
      loadEmbedScript();
      setLoaded(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMessage = (event: MessageEvent) => {
      try {
        const origin = event.origin || "";
        if (!/leadconnectorhq\.com|msgsndr\.com/i.test(origin)) return;
        const data = event.data;
        const text = typeof data === "string" ? data : JSON.stringify(data ?? "");
        if (
          /appointment[._-]?(scheduled|booked|confirmed)|booking[._-]?(success|confirmed|complete)|formSubmitted/i.test(
            text,
          )
        ) {
          phCapture("booking_created", { booking_source: "book_a_call_modal", calendar: "ghl" });
          onOpenChange(false);
          navigate({ to: "/thank-you" });
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open, navigate, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={descriptionId}
        className="p-0 gap-0 max-w-3xl w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] h-[calc(100svh-1rem)] sm:h-auto sm:max-h-[90vh] overflow-hidden rounded-2xl [&>button.absolute]:hidden"
      >
        <DialogHeader className="flex flex-row items-center justify-between gap-2 px-4 py-3 border-b bg-background">
          <div>
            <DialogTitle className="text-base font-semibold">
              Book A Call With CRS Energy
            </DialogTitle>
            <DialogDescription id={descriptionId} className="sr-only">
              Select a time for CRS Energy to call you back.
            </DialogDescription>
          </div>
          <DialogClose
            className="rounded-full p-1.5 hover:bg-secondary transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </DialogClose>
        </DialogHeader>
        <div
          className="relative flex-1 min-h-0 bg-background h-[calc(100svh-4rem)] sm:h-[80vh] sm:min-h-[640px]"
          aria-busy={!loaded}
        >
          {open && (
            <iframe
              src={BOOKING_URL}
              title="Book A Call With CRS Energy"
              scrolling="yes"
              onLoad={() => setLoaded(true)}
              style={{
                width: "100%",
                height: "100%",
                border: 0,
                display: "block",
              }}
            />
          )}
          {!loaded && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background px-6 text-center">
              <div className="max-w-sm">
                <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
                <p className="text-base font-semibold text-foreground">
                  Opening The Booking Calendar
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  This should only take a moment. If it is slow, you can call us instead.
                </p>
                <a
                  href={SITE.phoneTel}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground hover:bg-accent/90"
                >
                  <Phone className="h-4 w-4" aria-hidden />
                  Call Now
                </a>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BookACallButton({
  className,
  children,
  icon = true,
  label = "Book a Call",
}: {
  className?: string;
  children?: ReactNode;
  icon?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onMouseEnter={loadEmbedScript}
        onFocus={loadEmbedScript}
        onTouchStart={loadEmbedScript}
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-full px-3 xl:px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap",
          "border border-input bg-background text-foreground hover:bg-secondary",
          className,
        )}
      >
        {icon && <CalendarClock className="h-4 w-4" aria-hidden />}
        {children ?? label}
      </button>
      <BookACallDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
